import React from 'react';
import type { TourProps } from 'antd';

export type TourRefs = {
  introRef: React.RefObject<HTMLElement>;
  queryRef: React.RefObject<HTMLElement>;
  grantRef: React.RefObject<HTMLElement>;
  piRef: React.RefObject<HTMLElement>;
  impactTypeRef: React.RefObject<HTMLElement>;
  landscapeRef: React.RefObject<HTMLElement>;
  entityRef: React.RefObject<HTMLElement>;
  historicalRef: React.RefObject<HTMLElement>;
  helpRef: React.RefObject<HTMLElement>;
};



export const buildTourSteps = (refs: TourRefs): TourProps['steps'] => [
  {
    title: 'Welcome to FtF System!',
    description:
      'FtF (Funding the Frontier) System provides an integrated platform to explore how funded projects lead to different forms of scientific and societal impact. Use this guided tour to learn the six coordinated views.',
    target: () => refs.introRef.current as HTMLElement,
    placement: 'bottom',
  },
  {
    title: 'Query View',
    description:
      'Filter grants by funding agency, start year, or funding amount to set the scope for all subsequent analyses.',
    target: () => refs.queryRef.current as HTMLElement,
    placement: 'right',
  },
  {
    title: 'Grant View',
    description:
      <div>
        <p>Grants are clustered into bubbles by research area. Bubble size encodes funding amount, and position reflects topic similarity. </p>
        <p><span style={{ color: '#7F5539', fontWeight: 'bold' }}>Hover</span> to show tooltip. </p>
        <p><span style={{ color: '#7F5539', fontWeight: 'bold' }}>Scroll</span> to zoom in and out. </p>
      </div>,
      // 'Grants are clustered into bubbles by research area. Bubble size encodes funding amount, and position reflects topic similarity. Hover to show tooltip. Scroll to zoom in and out.',
    target: () => refs.grantRef.current as HTMLElement,
    placement: 'right',
  },
  {
    title: 'PI View',
    description:
      'Explore funded Principal Investigators (PIs) with profiles showing demographics, affiliation, research field, and impact metrics.',
    target: () => refs.piRef.current as HTMLElement,
    placement: 'right',
  },
  {
    title: 'PI List',
    description:
      'Switch to PI List to view individual PIs with quick sorting, filtering, and searching.',
    target: () => refs.piRef.current as HTMLElement,
    placement: 'right',
  },
  {
    title: 'Impact Type View',
    description:
      <div>
        <p>Switch between direct and broader impacts to <span style={{ color: '#7F5539', fontWeight: 'bold' }}>select</span> impact type of interest (e.g., marketplace impact and policy impact). The selected impact type will be shown in the center of Impact Landscape View. </p>
        <p>Bar charts compare each grant's impact metrics against the global baseline (over the all dataset). </p>
      </div>,
      // 'Switch between direct and broader impacts to select impact type of interest (e.g., marketplace impact and policy impact). The selected impact type will be shown in the center of Impact Landscape View. Bar charts compare each grant\'s impact metrics against the global baseline (over the all dataset).',
    target: () => refs.impactTypeRef.current as HTMLElement,
    placement: 'left',
  },
  {
    title: 'Impact Landscape View',
    description:
      <div>
        <p>
          A hybrid network layout where grant topic nodes are placed in the center, surrounded by impact nodes grouped by hierarchical topic structure (bubble treemap). Links show citation flows.
        </p>
        <p>
          <span style={{ color: '#7F5539', fontWeight: 'bold' }}>Hover</span> nodes to show tooltip of word cloud.
        </p>
        <p>
          <span style={{ color: '#7F5539', fontWeight: 'bold' }}>Click</span> a node to show citation linkages and related metadata in the side view (Impact Entity View).
        </p>
        <p>
          <span style={{ color: '#7F5539', fontWeight: 'bold' }}>Scroll</span> to zoom in and out.
        </p>
        <p>
          <span style={{ color: '#7F5539', fontWeight: 'bold' }}>Pan</span> to move the view.
        </p>
      </div>,
    target: () => refs.landscapeRef.current as HTMLElement,
    placement: 'right',
  },
  {
    title: 'Prediction Mode',
    description:
    <div>
      <p>Switch to prediction mode to explore future investment opportunities. </p>
      <p>Click an impact topic node to display the distribution of recent high-prediction-score grants (highlighted with a purple ring) and the corresponding PI information (listed in the left sidebar: PI List). </p>
      <p>Hover over a grant node to view a word cloud of recent high-prediction-score grants. </p>
    </div>,
      // 'Switch to prediction mode to explore future investment opportunities. Click an impact topic node to display the distribution of recent high-prediction-score grants (highlighted with a purple ring) and the corresponding PI information (listed in the left sidebar: PI List). Hover over a grant node to view a tooltip showing a word cloud of recent high-prediction-score grants.',
    target: () => refs.historicalRef.current as HTMLElement,
    placement: 'right',
  },
  {
    title: 'Impact Entity View',
    description:
      <div>
        <p>Provide additional information of all or the selected impact topic nodes (e.g., paper, patent, clinical trial, policy, newsfeed). </p>
        <p><span style={{ color: '#7F5539', fontWeight: 'bold' }}>Cross-view interaction:</span> click an impact topic node in the Impact Landscape View to update barchart in this view.</p>
      </div>,
      // 'Provide additional information of all or the selected impact topic nodes (e.g., paper, patent, clinical trial, policy, newsfeed). Cross-view interaction: click an impact topic node in the Impact Landscape View to update barchart in this view.',
    target: () => refs.entityRef.current as HTMLElement,
    placement: 'left',
  },
  {
    title: 'Help',
    description:
      'Click to view the help page.',
    target: () => refs.helpRef.current as HTMLElement,
    placement: 'right',
  },
  {
    title: 'Start Querying!',
    description:
      'Now, start by selecting a funding agency to begin exploring the system!',
    target: () => refs.queryRef.current as HTMLElement,
    placement: 'right',
  }
];


